import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaRegCommentDots } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import SelecteurPieces, { verifierPieces } from './SelecteurPieces';

/**
 * Dépôt d'une demande libre « Autres » par son titulaire (Yros/Kavero#433).
 *
 * Un objet en texte libre, un motif pris dans la liste fermée, des pièces
 * facultatives. Le motif n'est jamais saisi librement, pour la même raison que
 * sur le prêt : un motif libre peut révéler la religion, la santé ou la
 * situation familiale, que la loi 2008-12 protège en son article 40. C'est
 * l'objet qui est libre, et il dit ce que le salarié choisit de dire.
 *
 * L'écran ne vérifie que ce qu'il peut voir : un objet vide ou démesuré, un
 * motif hors liste. Le reste — les pièces, le périmètre — est jugé par Odoo, et
 * ses refus s'affichent mot pour mot.
 */

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

// Même liste, et dans le même ordre, que celle qu'Odoo fait respecter (celle
// du prêt) : deux listes qui divergent feraient découvrir la bonne par un refus.
const MOTIFS = ['famille', 'logement', 'scolarite', 'equipement', 'autre'];
const LONGUEUR_MAX_OBJET = 2000;

function DemandeAutres({ onDepot }) {
  const { t } = useTranslation();
  const [objet, setObjet] = useState('');
  const [motif, setMotif] = useState('');
  const [pieces, setPieces] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    const objetNet = objet.trim();
    if (!objetNet) {
      setErreur(t('demandeAutres.objetManquant'));
      return;
    }
    if (objetNet.length > LONGUEUR_MAX_OBJET) {
      setErreur(t('demandeAutres.objetTropLong'));
      return;
    }
    if (!MOTIFS.includes(motif)) {
      setErreur(t('demandeAutres.motifManquant'));
      return;
    }
    const refusPieces = verifierPieces(pieces, t);
    if (refusPieces) {
      setErreur(refusPieces);
      return;
    }

    setEnvoi(true);
    try {
      const demande = { type: 'autres', contenu: { objet: objetNet, motif } };
      // Sans pièce, l'appel est celui d'avant : un seul argument.
      const reponse = pieces.length
        ? await apiService.creerDemandeRh(demande, pieces)
        : await apiService.creerDemandeRh(demande);
      if (reponse && reponse.ok) {
        setSucces(t('demandeAutres.succes'));
        setObjet('');
        setMotif('');
        setPieces([]);
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandeAutres.erreurGenerique'));
      }
    } catch (e) {
      setErreur(e?.response?.data?.message || t('demandeAutres.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="flex items-center gap-2">
        <FaRegCommentDots className="text-[--color-primary]" />
        <h2 className="text-base sm:text-lg font-semibold">{t('demandeAutres.titre')}</h2>
      </div>
      <p className="text-sm text-gray-600">{t('demandeAutres.explication')}</p>

      <form data-testid="autres-formulaire" onSubmit={soumettre} className="space-y-4">
        <label className="block text-sm font-medium">
          {t('demandeAutres.objet')}
          <textarea
            data-testid="autres-objet"
            rows={4}
            value={objet}
            onChange={(e) => setObjet(e.target.value)}
            className={CLASSE_CHAMP}
          />
        </label>
        <label className="block text-sm font-medium sm:max-w-xs">
          {t('demandeAutres.motif')}
          <select
            data-testid="autres-motif"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            className={CLASSE_CHAMP}
          >
            <option value="">{t('demandeAutres.motifChoisir')}</option>
            {MOTIFS.map((code) => (
              <option key={code} value={code}>
                {t(`demandePret.motifs.${code}`)}
              </option>
            ))}
          </select>
        </label>

        <SelecteurPieces idPrefix="autres" fichiers={pieces} onChange={setPieces} />

        {erreur && (
          <p
            data-testid="autres-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}
        {succes && (
          <p
            data-testid="autres-succes"
            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
          >
            {succes}
          </p>
        )}

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            data-testid="autres-soumettre"
            disabled={envoi}
            className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} />
            {envoi ? t('demandeAutres.envoi') : t('demandeAutres.envoyer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DemandeAutres;
